import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";
import { terminate, saveNodeType, isHoverForInteractiveType } from "./utils";
import EachCallout from "./wrapper/EachCallout";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class Callout extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawCallout = this.handleDrawCallout.bind(this);

    this.handleDragBubble = this.handleDragBubble.bind(this);
    this.handleDragStem = this.handleDragStem.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.handleTextChange = this.handleTextChange.bind(this);

    this.terminate = terminate.bind(this);
    this.getSelectionState = isHoverForInteractiveType("callouts").bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = [];

    this.state = {
      current: null,
      override: null,
    };
  }

  handleDrawCallout(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.x1)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          ...current,
          x2: xyValue[0],
          y2: xyValue[1],
        },
      });
    }
  }

  handleDragBubble(index, xy) {
    const { callouts } = this.props;
    const item = callouts[index];

    this.setState({
      override: {
        index,
        ...xy,
        text: item.text, // Preserve text when dragging
      },
    });
  }

  handleDragStem(index, newXY) {
    const { callouts } = this.props;
    const item = callouts[index];

    this.setState({
      override: {
        index,
        x1: newXY[0], // Update bar anchor position
        y1: newXY[1],
        x2: item.x2, // Keep bubble position
        y2: item.y2,
        text: item.text,
      },
    });
  }

  handleDragComplete(moreProps) {
    const { callouts } = this.props;
    const { override } = this.state;
    if (isDefined(override)) {
      const { index, ...rest } = override;

      const newCallouts = callouts.map((each, idx) =>
        idx === index ? { ...each, ...rest, selected: true } : each,
      );
      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newCallouts, moreProps);
        },
      );
    }
  }

  handleTextChange(index, newText) {
    const { callouts } = this.props;
    const newCallouts = callouts.map((each, idx) =>
      idx === index ? { ...each, text: newText, selected: true } : each,
    );
    this.props.onComplete(newCallouts);
  }

  handleStart(xyValue, moreProps) {
    const { current } = this.state;
    if (isNotDefined(current) || isNotDefined(current.x1)) {
      this.mouseMoved = false;

      // Manually snap first point (bar anchor) to nearest bar
      const { xAccessor, currentItem } = moreProps;
      const snappedX1 = xAccessor(currentItem);

      this.setState(
        {
          current: {
            x1: snappedX1, // Snapped to bar
            y1: xyValue[1], // Free Y position
            x2: null,
            y2: null,
            text: "Texto",
          },
        },
        () => {
          this.props.onStart(moreProps);
        },
      );
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { callouts, appearance } = this.props;
    const { current } = this.state;

    if (this.mouseMoved && isDefined(current) && isDefined(current.x1)) {
      const currentAppearance = appearance;

      const newCallouts = callouts.concat({
        ...current,
        x2: xyValue[0],
        y2: xyValue[1],
        selected: true,
        appearance: currentAppearance,
      });

      this.setState(
        {
          current: null,
        },
        () => {
          this.props.onComplete(newCallouts, moreProps, e);
        },
      );
    }
  }

  render() {
    const { enabled, callouts, appearance, hoverText } = this.props;
    const { current, override } = this.state;

    const hoverTextWithDefaults = {
      ...Callout.defaultProps.hoverText,
      ...hoverText,
    };

    const tempCallout =
      isDefined(current) && isDefined(current.x1) ? (
        <EachCallout
          interactive={false}
          {...current}
          appearance={appearance}
          hoverText={hoverTextWithDefaults}
        />
      ) : null;

    return (
      <g>
        {callouts.map((each, idx) => {
          // Deep merge appearance
          const eachAppearance = isDefined(each.appearance)
            ? {
                ...appearance,
                ...each.appearance,
                stroke:
                  typeof each.appearance.stroke === "string"
                    ? { ...appearance.stroke, color: each.appearance.stroke }
                    : typeof each.appearance.stroke === "object"
                      ? { ...appearance.stroke, ...each.appearance.stroke }
                      : appearance.stroke,
                fill:
                  typeof each.appearance.fill === "string"
                    ? { ...appearance.fill, color: each.appearance.fill }
                    : typeof each.appearance.fill === "object"
                      ? { ...appearance.fill, ...each.appearance.fill }
                      : appearance.fill,
                font:
                  typeof each.appearance.font === "string"
                    ? { ...appearance.font, color: each.appearance.font }
                    : typeof each.appearance.font === "object"
                      ? { ...appearance.font, ...each.appearance.font }
                      : appearance.font,
              }
            : appearance;

          const props = {
            key: idx,
            ref: this.saveNodeType(idx),
            index: idx,
            ...each,
            appearance: eachAppearance,
            hoverText: hoverTextWithDefaults,

            selected: each.selected,
            hovering: each.hovering,

            onDragBubble: this.handleDragBubble,
            onDragStem: this.handleDragStem,
            onDragComplete: this.handleDragComplete,
            onTextChange: this.handleTextChange,

            ...(isDefined(override) && override.index === idx ? override : {}),
          };

          return <EachCallout {...props} />;
        })}
        {tempCallout}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          shouldDisableSnap={() => true}
          onMouseDown={this.handleStart}
          onMouseMove={this.handleDrawCallout}
          onClick={this.handleEnd}
          r={4}
          stroke={appearance.stroke?.color || appearance.stroke || "#000000"}
          strokeWidth={2}
          opacity={1}
          data-evo-id="interactive-callout"
        />
      </g>
    );
  }
}

Callout.propTypes = {
  enabled: PropTypes.bool.isRequired,
  callouts: PropTypes.array.isRequired,
  appearance: PropTypes.object.isRequired,

  onStart: PropTypes.func,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,

  hoverText: PropTypes.object.isRequired,
};

Callout.defaultProps = {
  callouts: [],
  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique e arraste para criar balão",
    selectedText: "",
  },
};

export default Callout;
